import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useTableViewService,
  useUpdateTableViewService,
} from '@/services/tableViews';
import {
  useEffect,
  useState,
  type ChangeEvent,
  type FC,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

export const EditTableViewModal: FC<{
  tableViewId: string | undefined;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}> = ({ tableViewId, trigger, open: controlledOpen, onOpenChange }) => {
  const { tableView } = useTableViewService(tableViewId ?? '');
  const { updateTableView, isUpdateTableViewPending } =
    useUpdateTableViewService();
  const [nameValue, setNameValue] = useState(tableView?.name ?? '');
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled open state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
  };

  const handleSave = () => {
    if (!tableViewId) {
      return;
    }
    updateTableView(
      {
        tableViewId,
        data: { name: nameValue },
      },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
        onError: () => {
          toast.error('Failed to update table view');
        },
      }
    );
  };

  const errorTooltip = !nameValue.trim() ? 'Name is required' : undefined;

  const isSaveDisabled = isUpdateTableViewPending || !!errorTooltip;

  useEffect(() => {
    if (tableView?.name) {
      setNameValue(tableView.name);
    }
  }, [tableView?.name]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Table View</DialogTitle>
        </DialogHeader>
        <FlexCol gap="2">
          <Input label="Name" value={nameValue} onChange={handleNameChange} />
        </FlexCol>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button onClick={handleSave} disabled={isSaveDisabled}>
                  Save
                </Button>
              </div>
            </TooltipTrigger>
            {errorTooltip && <TooltipContent>{errorTooltip}</TooltipContent>}
          </Tooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
